import { Injectable } from '@angular/core';
import { DateTimeFormat, DateTimeService } from '@examdojo/core/date-time';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { SupabaseSelectQueryConfig } from '@examdojo/core/supabase';
import { UserQuery } from '@examdojo/user';
import { Observable } from 'rxjs';
import { finalize, first, map, switchMap, tap } from 'rxjs/operators';
import { DailyHoursMap } from '../payments.model';
import { PayoutRequestHttpService } from './payout-request-http.service';
import {
  PayoutRequestData,
  PayoutRequestHttpModel,
  PayoutRequestInsertModel,
  PayoutRequestListViewHttpModel,
  PayoutRequestStatus,
  PayoutRequestStoreModel,
  PayoutRequestUIModel,
  PayoutRequestUpdateModel,
} from './payout-request.model';
import { PayoutRequestStore } from './payout-request.store';

@Injectable({
  providedIn: 'root',
})
export class PayoutRequestService {
  constructor(
    private readonly httpService: PayoutRequestHttpService,
    private readonly store: PayoutRequestStore,
    private readonly userQuery: UserQuery,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly dateTimeService: DateTimeService,
  ) {}

  loadPayoutRequests(): Observable<PayoutRequestStoreModel[]> {
    this.store.setLoading(true);

    return this.httpService.fetchAll().pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'validation.entity.payout_request',
        action: 'fetch',
      }),
      map((requests) => {
        // Sort by created date (newest first)image.png
        const sortedRequests = requests.sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
        const storeModelRequests = sortedRequests.map((request) => this.mapToStoreModel(request));

        this.store.set(storeModelRequests);

        return storeModelRequests;
      }),
      finalize(() => {
        this.store.setLoading(false);
      }),
    );
  }

  loadCurrentUserPayoutRequests(): Observable<PayoutRequestStoreModel[]> {
    this.store.setLoading(true);

    return this.userQuery.active$.pipe(
      first(Boolean),
      switchMap((user) =>
        this.httpService.fetchByUserId(user.id).pipe(
          this.errorHandlerService.setHttpErrorMetadata({
            entity: 'validation.entity.payout_request',
            action: 'fetch',
          }),
          map((requests) => {
            const sortedRequests = requests.sort(
              (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
            );
            const storeModelRequests = sortedRequests.map((request) => this.mapToStoreModel(request));

            this.store.set(storeModelRequests);

            return storeModelRequests;
          }),
        ),
      ),
      finalize(() => {
        this.store.setLoading(false);
      }),
    );
  }

  fetchPaginatedListView(
    selectQueryConfig: SupabaseSelectQueryConfig = {},
  ): Observable<{ data: PayoutRequestUIModel[]; totalCount: number }> {
    return this.httpService.fetchPaginatedListView(selectQueryConfig).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'validation.entity.payout_request',
        action: 'fetch',
      }),
      map(({ data, totalCount }) => ({
        data: data.map((request) => this.mapToUIModel(request)),
        totalCount,
      })),
    );
  }

  createPayoutRequest(data: PayoutRequestData): Observable<PayoutRequestStoreModel> {
    this.store.setLoading(true);

    return this.userQuery.active$.pipe(
      first(Boolean),
      switchMap((user) => {
        const insertData: PayoutRequestInsertModel = {
          user_id: user.id,
          period: data.period,
          hours: data.hours,
          status: 'DRAFT',
        };

        return this.httpService.create(insertData);
      }),
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'validation.entity.payout_request',
        action: 'create',
      }),
      map((request) => this.mapToStoreModel(request)),
      tap((request) => {
        this.store.create(request);
      }),
      finalize(() => {
        this.store.setLoading(false);
      }),
    );
  }

  updatePayoutRequestWithHours(
    requestId: string,
    formData: Partial<PayoutRequestData>,
  ): Observable<PayoutRequestStoreModel> {
    const updateData: PayoutRequestUpdateModel = {};

    if (formData.hours !== undefined) {
      updateData.hours = formData.hours;
    }

    return this.updatePayoutRequest(requestId, updateData);
  }

  approvePayoutRequest(requestId: string, reason: string): Observable<PayoutRequestStoreModel> {
    return this.updatePayoutRequest(requestId, { status: 'APPROVED', status_reason: reason });
  }

  rejectPayoutRequest(requestId: string, reason: string): Observable<PayoutRequestStoreModel> {
    return this.updatePayoutRequest(requestId, { status: 'REJECTED', status_reason: reason });
  }

  submitPayoutRequest(requestId: string): Observable<PayoutRequestStoreModel> {
    return this.updatePayoutRequest(requestId, { status: 'WAITING_FOR_APPROVAL' });
  }

  withdrawPayoutRequest(requestId: string): Observable<PayoutRequestStoreModel> {
    return this.updatePayoutRequest(requestId, { status: 'DRAFT' });
  }

  deletePayoutRequest(requestId: string): Observable<void> {
    this.store.setLoading(true);

    return this.httpService.delete(requestId).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'validation.entity.payout_request',
        action: 'delete',
      }),
      tap(() => {
        this.store.delete(requestId);
      }),
      finalize(() => {
        this.store.setLoading(false);
      }),
    );
  }

  updateExtraBonus(requestId: string, amount: number, reason: string): Observable<PayoutRequestStoreModel> {
    return this.updatePayoutRequest(requestId, {
      extra_bonus_in_eur: amount,
      extra_bonus_reason: reason,
    });
  }

  getCurrentPeriod(): string {
    return this.dateTimeService.__format(new Date(), 'yyyy-MM');
  }

  getPeriodDisplayName(period: string): string {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return this.dateTimeService.formatDate(date, DateTimeFormat.LongMonthYear);
  }

  reset(): void {
    this.store.reset();
  }

  private updatePayoutRequest(
    requestId: string,
    updateData: PayoutRequestUpdateModel,
  ): Observable<PayoutRequestStoreModel> {
    this.store.setLoading(true);

    return this.httpService.update(requestId, updateData).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'validation.entity.payout_request',
        action: 'update',
      }),
      map((request) => this.mapToStoreModel(request)),
      tap((request) => {
        this.store.update(request.id, request);
      }),
      finalize(() => {
        this.store.setLoading(false);
      }),
    );
  }

  private mapToStoreModel(request: PayoutRequestHttpModel | PayoutRequestListViewHttpModel): PayoutRequestStoreModel {
    return {
      ...request,
      hours: (request.hours ?? {}) as DailyHoursMap,
    };
  }

  private mapToUIModel(request: PayoutRequestListViewHttpModel): PayoutRequestUIModel {
    const hours = (request.hours ?? {}) as DailyHoursMap;
    const totalHours = Object.values(hours).reduce((sum, value) => sum + (value ?? 0), 0);
    const appliedHourlyRate = request.current_hourly_rate ?? 0;
    const estimatedPayout = totalHours * appliedHourlyRate;

    const calculatedFinalPayout =
      request.status === 'DRAFT' ? estimatedPayout + (request.extra_bonus_in_eur ?? 0) : request.final_payout_in_eur;

    return {
      ...request,
      hours,
      totalHours,
      estimatedPayout,
      appliedHourlyRate,
      calculatedFinalPayout,
      isEditable: request.status === 'DRAFT',
      canSubmit: request.status === 'DRAFT',
      statusLabel: this.getStatusLabel(request.status),
    };
  }

  private getStatusLabel(status: PayoutRequestStatus): string {
    const labels: Record<PayoutRequestStatus, string> = {
      DRAFT: 'Draft',
      WAITING_FOR_APPROVAL: 'Waiting for Approval',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      PAYMENT_WAITING_FOR_APPROVAL: 'Payment Pending',
      PAYMENT_IN_PROGRESS: 'Payment in Progress',
      PAYMENT_SUCCEEDED: 'Payment Completed',
      PAYMENT_FAILED: 'Payment Failed',
    };
    return labels[status] || status;
  }
}
